import { configureClient } from './api-client'
import { setWidgetCustomer } from './ofetch-client'
import { en } from './i18n/en'
import { ar } from './i18n/ar'
import { mountWidget, unmountWidget, getShadowRoot } from './shadow-dom'
import { themeToVars } from './theme'
import { createFab, setFabOpen } from './ui/fab'
import { createTicketForm, createSuccessView } from './ui/ticket-form'
import { createTrackerView } from './ui/tracker'
import { createPortalView } from './ui/portal'
import { getTrackingTokens, loadWidgetConfig, saveWidgetConfig, resolvePreferences } from './storage'
import { normalizePosition } from './position'
import { getRegistry, type WidgetRegistry } from './registry'
import type {
  ReqdeskWidgetConfig,
  ThemeConfig,
  CustomerConfig,
  WidgetEvent,
  EventCallback,
  TicketResult,
  DisplayMode,
  SheetSide,
  WidgetPreferences,
  MenuActionInput,
  TriggerMountOptions,
} from './types'

export type {
  ReqdeskWidgetConfig,
  ThemeConfig,
  CustomerConfig,
  WidgetEvent,
  EventCallback,
  TicketResult,
  DisplayMode,
  SheetSide,
  WidgetPreferences,
  MenuActionInput,
  TriggerMountOptions,
}

const translations: Record<string, Record<string, string>> = { en, ar }

let config: ReqdeskWidgetConfig | null = null
let isOpen = false
let currentLang = 'en'
let fab: HTMLButtonElement | null = null
let panel: HTMLElement | null = null
let mount: HTMLElement | null = null
let currentTab: 'form' | 'tracker' = 'form'
let pendingTrackToken: string | undefined
let registry: WidgetRegistry | null = null

function t(key: string): string {
  const overrides = config?.translations
  if (overrides?.[key]) return overrides[key]
  const lang = translations[currentLang] ?? translations.en
  return lang[key] ?? key
}

function emit(event: WidgetEvent, data?: unknown) {
  registry?.emitEvent(event, data)
}

function deriveApiUrl(): string {
  return window.location.origin
}

function extractProjectId(): string {
  return '_current'
}

function renderPanel() {
  if (!mount || !config) return

  // Remove the previous panel only — never the FAB. `mount` holds both the
  // panel and the FAB as siblings; wiping `mount` was the source of the
  // "FAB disappears after opening once" bug because open() → renderPanel()
  // would also remove the FAB created during init().
  if (panel && panel.parentNode === mount) {
    mount.removeChild(panel)
    panel = null
  }

  const position = normalizePosition(config.position)

  if (!config.inline) {
    panel = document.createElement('div')
    panel.className = `rqd-panel rqd-${position}`
    panel.style.cssText = themeToVars(config.theme)

    const header = document.createElement('div')
    header.className = 'rqd-header'
    const title = document.createElement('span')
    title.className = 'rqd-header-title'
    title.textContent = t('widget.title')
    header.appendChild(title)

    const closeBtn = document.createElement('button')
    closeBtn.className = 'rqd-header-close'
    closeBtn.textContent = '\u2715'
    closeBtn.addEventListener('click', close)
    header.appendChild(closeBtn)
    panel.appendChild(header)

    const body = document.createElement('div')
    body.className = 'rqd-body'

    const isPortal = config.widget === 'support-portal' && config.customer?.email

    if (isPortal) {
      renderPortal(body)
    } else {
      const tabs = document.createElement('div')
      tabs.className = 'rqd-tabs'

      const formTab = document.createElement('button')
      formTab.className = `rqd-tab${currentTab === 'form' ? ' rqd-active' : ''}`
      formTab.textContent = t('widget.newTicket')
      formTab.addEventListener('click', () => switchTab('form', body, tabs))

      const trackerTab = document.createElement('button')
      trackerTab.className = `rqd-tab${currentTab === 'tracker' ? ' rqd-active' : ''}`
      trackerTab.textContent = t('widget.trackTicket')
      trackerTab.addEventListener('click', () => switchTab('tracker', body, tabs))

      tabs.appendChild(formTab)
      tabs.appendChild(trackerTab)
      panel.appendChild(tabs)

      if (currentTab === 'form') renderForm(body)
      else renderTracker(body)
    }

    panel.appendChild(body)
    mount.appendChild(panel)
  } else {
    const container = typeof config.container === 'string'
      ? document.querySelector(config.container)
      : config.container

    if (container) {
      const inline = document.createElement('div')
      inline.className = 'rqd-inline'
      inline.style.cssText = themeToVars(config.theme)

      const body = document.createElement('div')
      body.className = 'rqd-body'
      renderForm(body)
      inline.appendChild(body)

      container.appendChild(inline)
      return
    }
  }
}

function renderPortal(container: HTMLElement) {
  if (!config) return

  const projectId = extractProjectId()

  const portalView = createPortalView(
    t,
    async () => [],
    async (id: string) => ({
      id,
      ticketNumber: '',
      title: '',
      status: '',
      priority: '',
      createdAt: '',
      replies: [],
    }),
    async (_ticketId: string, _body: string) => { /* will call submitReply via API client */ },
    projectId,
    (data) => emit('ticket:created', data),
    (err) => emit('error', err),
    30000,
  )
  container.appendChild(portalView)
}

function switchTab(tab: 'form' | 'tracker', body: HTMLElement, tabs: HTMLElement) {
  currentTab = tab
  pendingTrackToken = undefined

  const tabBtns = tabs.querySelectorAll('.rqd-tab')
  tabBtns.forEach((btn, i) => {
    btn.classList.toggle('rqd-active', (i === 0 && tab === 'form') || (i === 1 && tab === 'tracker'))
  })

  while (body.firstChild) body.removeChild(body.firstChild)
  if (tab === 'form') renderForm(body)
  else renderTracker(body)
}

function renderForm(container: HTMLElement) {
  if (!config) return

  const projectId = extractProjectId()
  const form = createTicketForm(
    projectId,
    t,
    (result: TicketResult) => {
      emit('ticket:created', result)
      renderSuccess(container, result)
    },
    (err) => { emit('error', err) },
  )
  container.appendChild(form)
}

function renderTracker(container: HTMLElement) {
  const view = createTrackerView(
    t,
    (data) => emit('ticket:tracked', data),
    (data) => emit('reply:sent', data),
    (err) => emit('error', err),
    undefined,
    pendingTrackToken,
  )
  container.appendChild(view)
}

function renderSuccess(container: HTMLElement, result: TicketResult) {
  while (container.firstChild) container.removeChild(container.firstChild)

  const view = createSuccessView(
    result,
    t,
    () => close(),
    result.trackingToken
      ? () => {
          pendingTrackToken = result.trackingToken
          if (panel) {
            const body = panel.querySelector('.rqd-body') as HTMLElement
            const tabs = panel.querySelector('.rqd-tabs') as HTMLElement
            if (body && tabs) switchTab('tracker', body, tabs)
          }
        }
      : undefined,
  )
  container.appendChild(view)
}

export function init(cfg: ReqdeskWidgetConfig): void {
  if (config) return

  const saved = loadWidgetConfig(cfg.apiKey)
  const resolvedPosition = normalizePosition(cfg.position ?? saved?.position)
  config = {
    ...cfg,
    position: resolvedPosition,
    language: cfg.language ?? saved?.language ?? 'en',
    widget: cfg.widget ?? saved?.widget ?? 'ticket-form',
    theme: { ...saved?.theme, ...cfg.theme },
  }
  currentLang = config.language ?? 'en'

  saveWidgetConfig(cfg.apiKey, {
    position: resolvedPosition,
    language: config.language,
    widget: config.widget,
    theme: config.theme,
  })

  // Initialize the shared registry with resolved preferences and host hooks.
  registry = getRegistry()
  registry.setApiKeyForStorage(cfg.apiKey)
  registry.setInitialPreferences(cfg.initialPreferences ?? {})
  registry.setConfigDisplay(cfg.display)
  registry.setMenuCloseOnAction(cfg.menuCloseOnAction ?? true)
  registry.setHostPreferencesCallback(cfg.onPreferencesChange)
  registry.setContext({
    user: cfg.customer ? { email: cfg.customer.email, name: cfg.customer.name } : null,
    locale: currentLang,
  })
  const resolvedPrefs = resolvePreferences({
    apiKey: cfg.apiKey,
    initialPreferences: cfg.initialPreferences,
    userPreferences: cfg.userPreferences,
  })
  registry.initPreferencesSnapshot(resolvedPrefs)

  // Register any actions supplied up front.
  if (cfg.actions && cfg.actions.length > 0) {
    for (const action of cfg.actions) registry.addAction(action)
  }

  const apiUrl = deriveApiUrl()
  configureClient(apiUrl, cfg.apiKey)
  setWidgetCustomer(cfg.customer ?? null)

  if (cfg.inline) {
    renderPanel()
    return
  }

  const { mount: m } = mountWidget({ zIndex: cfg.theme?.zIndex })
  mount = m

  const shadow = getShadowRoot()
  if (shadow) {
    const root = shadow.querySelector('.rqd-root') as HTMLElement
    if (root) root.style.cssText = themeToVars(cfg.theme)
  }

  const host = document.getElementById('reqdesk-widget')
  if (host && (currentLang === 'ar')) {
    host.setAttribute('dir', 'rtl')
  }

  const tokens = getTrackingTokens(cfg.apiKey)
  if (tokens.length > 0) currentTab = 'tracker'

  // Respect hideFab (FR-018a): when true, skip FAB rendering entirely. Hosts can drive open/close
  // via mountTrigger() or the programmatic API.
  if (!cfg.hideFab) {
    const fabLabel = cfg.theme?.brandName ?? t('widget.title')
    fab = createFab(resolvedPosition, toggle, fabLabel, cfg.fabIcon)
    mount.appendChild(fab)
  }
}

export function open(): void {
  if (!config || isOpen) return
  isOpen = true
  if (fab) setFabOpen(fab, true, undefined, config.fabIcon)
  renderPanel()
  registry?.open()
}

export function close(): void {
  if (!isOpen) return
  isOpen = false
  const fabLabel = config?.theme?.brandName ?? t('widget.title')
  if (fab) setFabOpen(fab, false, fabLabel, config?.fabIcon)
  if (panel) {
    panel.remove()
    panel = null
  }
  registry?.close()
}

export function toggle(): void {
  if (isOpen) close()
  else open()
}

export function openMenu(): void {
  registry?.openMenu()
  if (!isOpen) open()
}

export function openAction(id: string, input?: unknown): void {
  registry?.openAction(id, input)
  if (!isOpen) open()
}

export function setDisplayMode(mode: DisplayMode, side?: SheetSide): void {
  registry?.setDisplayMode(mode, side)
}

export function setPreferences(next: Partial<WidgetPreferences>): void {
  registry?.setPreferences(next)
}

export function getPreferences(): WidgetPreferences {
  return registry?.getPreferences() ?? {}
}

export function addAction(action: MenuActionInput): () => void {
  if (!registry) return () => { /* init not called */ }
  return registry.addAction(action)
}

export function removeAction(id: string): void {
  registry?.removeAction(id)
}

export function setLanguage(lang: string): void {
  currentLang = lang
  if (config) {
    config.language = lang
    saveWidgetConfig(config.apiKey, { language: lang })
  }
  registry?.setContext({ locale: lang })
  const host = document.getElementById('reqdesk-widget')
  if (host) {
    host.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr')
  }
  if (isOpen && panel && mount) {
    panel.remove()
    panel = null
    renderPanel()
  }
}

export function setTheme(theme: Partial<ThemeConfig>): void {
  if (!config) return
  config.theme = { ...config.theme, ...theme }
  saveWidgetConfig(config.apiKey, { theme: config.theme })
  if (panel) {
    panel.style.cssText = themeToVars(config.theme)
  }
}

export function on(event: WidgetEvent, callback: EventCallback): () => void {
  if (!registry) {
    // Registry not initialized yet — buffer would be complex; treat pre-init subscriptions as a
    // no-op unsubscribe. Most callers register after init(); this matches widget init semantics.
    return () => { /* noop */ }
  }
  return registry.on(event, callback)
}

export function identify(customer: CustomerConfig): void {
  if (!config) return
  config.customer = customer
  setWidgetCustomer(customer)
  registry?.setContext({ user: { email: customer.email, name: customer.name } })
  if (isOpen && panel && mount) {
    panel.remove()
    panel = null
    renderPanel()
  }
}

export function destroy(): void {
  close()
  unmountWidget()
  registry?.destroy()
  registry = null
  config = null
  fab = null
  panel = null
  mount = null
  isOpen = false
}

export { mountTrigger } from './ui/trigger'
